import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTranslation } from 'react-i18next';
import { AlertCircle, MessageCircle } from 'lucide-react';

import { COUNTRY_CODES } from '../constants/phoneCountryCodes';

const getLoginSchema = (t: any) => z.object({
  countryCode: z.string().min(1),
  localPhone: z.string().min(1, t('login.invalidPhone', 'Please enter your phone number')),
  password: z.string().min(1, t('login.passwordRequired', 'Password cannot be empty')),
});

type LoginFormData = z.infer<ReturnType<typeof getLoginSchema>>;

const LoginFormPage: React.FC = () => {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [notRegistered, setNotRegistered] = useState(false);
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const loginSchema = useMemo(() => getLoginSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { countryCode: '+852' },
  });

  const onSubmit = async (data: LoginFormData) => {
    setError('');
    setNotRegistered(false);
    setLoading(true);
    try {
      const loggedInUser = await login(`${data.countryCode}${data.localPhone}`, data.password);
      if (loggedInUser) {
        const returnUrl = sessionStorage.getItem('returnUrl');
        sessionStorage.removeItem('returnUrl');
        // Sanitize returnUrl: don't redirect employer to helper-only routes and vice versa
        const helperOnlyPaths = ['/helper/profile', '/helper/saved-jobs', '/helper/recommended-jobs', '/helper/invitations'];
        const employerOnlyPaths = ['/job-postings/new', '/my-job-postings', '/my-candidates', '/interview-intents', '/my-applications'];
        const isInvalidForRole =
          (loggedInUser.role === 'EMPLOYER' && helperOnlyPaths.some(p => returnUrl?.startsWith(p))) ||
          (loggedInUser.role === 'HELPER' && employerOnlyPaths.some(p => returnUrl?.startsWith(p)));
        navigate(!returnUrl || isInvalidForRole ? '/home' : returnUrl);
      } else {
        setError(t('login.loginFailed', 'Login failed, please check your credentials'));
      }
    } catch (err: any) {
      const code = err.response?.data?.error;
      if (code === 'USER_NOT_REGISTERED') {
        setNotRegistered(true);
      } else {
        setNotRegistered(false);
        const errorMsg = err.response?.data?.message || err.message || t('login.loginFailed', 'Login failed, please check your credentials');
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
        <div className="flex overflow-hidden rounded-t-xl">
          <div className="w-[30%] flex items-center justify-center py-8">
            <MessageCircle className="w-10 h-10 text-primary" />
          </div>
          <div className="w-[70%] flex flex-col justify-center px-6 py-6">
            <p className="text-xl font-bold mb-1 leading-none">{t('common.welcome', '歡迎回來')}</p>
            <p className="text-sm text-muted-foreground">{t('login.subtitle', '連結社群，建立配對')}</p>
          </div>
        </div>
        <CardContent>
          {notRegistered && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('login.notRegistered', '此電話號碼尚未註冊。')}{' '}
                <button type="button" className="underline font-medium" onClick={() => navigate('/register')}>
                  {t('login.registerNow', '立即註冊')}
                </button>
              </AlertDescription>
            </Alert>
          )}
          {error && !notRegistered && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2 text-left">
              <Label htmlFor="localPhone">{t('login.phone', '電話號碼')}</Label>
              <div className="flex gap-2">
                <select
                  id="countryCode"
                  className="h-9 w-28 rounded-md border border-input bg-background px-2 text-sm"
                  {...register('countryCode')}
                >
                  {COUNTRY_CODES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <Input
                  id="localPhone"
                  type="tel"
                  placeholder="98765432"
                  className="flex-1"
                  {...register('localPhone')}
                />
              </div>
              {errors.localPhone && (
                <p className="text-sm bg-[#FFF0F0] text-[#872b57] rounded px-2 py-1">{errors.localPhone.message}</p>
              )}
            </div>
            <div className="space-y-2 text-left">
              <Label htmlFor="password">{t('login.password', 'Password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('login.passwordPlaceholder', 'Enter your password')}
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm bg-[#FFF0F0] text-[#872b57] rounded px-2 py-1">{errors.password.message}</p>
              )}
              <div className="text-right">
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto text-sm font-normal text-muted-foreground"
                  onClick={() => navigate('/forgot-password')}
                >
                  {t('login.forgotPassword', '忘記密碼？')}
                </Button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? t('common.loading', 'Loading...') : t('nav.login', 'Login')}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex justify-center border-t p-4 mt-4">
          <div className="text-sm text-muted-foreground">
            <span>{t('login.noAccount', "Don't have an account?")}</span>{' '}
            <Button 
              variant="link" 
              className="p-0 h-auto font-semibold text-primary" 
              onClick={() => navigate('/register')}
            >
              {t('nav.register', 'Register')}
            </Button>
          </div>
        </CardFooter>
      </Card>
      </main>
    </div>
  );
};

export default LoginFormPage;
